import { FormGroupUI } from "components/FormGroup";
import { FormGroupLabelUI } from "components/FormGroupLabel";
import { RhfInput } from "components/rhfs/RhfInput";
import { RhfRadioGroup } from "components/rhfs/RhfRadioGroup";
import { RhfSelect } from "components/rhfs/RhfSelect";
import { RhfTextarea } from "components/rhfs/RhfTextarea";
import { useSenderEmails } from "hooks/use-sender-emails-api";
import { DeliveryTiming } from "hooks/useEmailTemplatesRestApi";
import { type FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import type { OptionItem } from "types/common";

type CompanyGroupOption = {
  id: string;
  name: string;
  companyCount: number;
};

export type EmailTemplateFormValues = {
  id: string;
  name: string;
  from: string;
  distributionGroupId: string;
  subject: string;
  body: string;
  includeSender: boolean;
  deliveryTiming: DeliveryTiming;
};

type EmailTemplateFormFieldsProps = {
  companyGroups: CompanyGroupOption[];
};

const DELIVERY_TIMING_OPTIONS: OptionItem[] = [
  { label: "今すぐ配信", value: DeliveryTiming.Now },
  { label: "予約配信", value: DeliveryTiming.Scheduled },
];

export const EmailTemplateFormFields: FC<EmailTemplateFormFieldsProps> = ({
  companyGroups,
}) => {
  const { register } = useFormContext<EmailTemplateFormValues>();
  const { data: senderEmailsData } = useSenderEmails({ verifiedOnly: true });

  const groupOptions = useMemo(
    () =>
      companyGroups.map(group => ({
        label: `${group.name} (${group.companyCount}社)`,
        value: group.id,
      })),
    [companyGroups],
  );

  const fromOptions = useMemo(
    () =>
      (senderEmailsData?.items ?? []).map(sender => ({
        label: `${sender.displayName} (${sender.email})`,
        value: sender.email,
      })),
    [senderEmailsData],
  );

  return (
    <>
      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          メールテンプレート名 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <RhfInput name="name" placeholder="メールテンプレート名を入力" />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          配信先グループ <span className="text-error">*</span>
        </FormGroupLabelUI>
        <RhfSelect
          name="distributionGroupId"
          options={groupOptions}
          placeholder="配信先グループを選択..."
        />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          送信者 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <RhfSelect
          name="from"
          options={fromOptions}
          placeholder="送信者を選択..."
        />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <div className="form-control w-full max-w-xl">
          <label className="label cursor-pointer justify-start gap-4">
            <input
              type="checkbox"
              className="checkbox checkbox-primary"
              {...register("includeSender")}
            />
            <span className="label-text">自分にも送信する</span>
          </label>
        </div>
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          件名 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <RhfInput name="subject" placeholder="件名を入力" />
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          本文 <span className="text-error">*</span>
        </FormGroupLabelUI>
        <RhfTextarea name="body" rows={6} placeholder="本文を入力" />
        <div className="text-xs mt-1 text-base-content/60">
          変数: {"{{COMPANY}}"} = 会社名, {"{{NAME}}"} = 担当者名
        </div>
      </FormGroupUI>

      <FormGroupUI className="!space-x-0">
        <FormGroupLabelUI className="w-auto mr-0">
          配信タイミング
        </FormGroupLabelUI>
        <RhfRadioGroup
          name="deliveryTiming"
          options={DELIVERY_TIMING_OPTIONS}
        />
      </FormGroupUI>
    </>
  );
};
